export interface RGBA {
  r: number
  g: number
  b: number
  a: number
}

export function hexToRgba(hex: string): RGBA {
  const cleaned = hex.replace('#', '')
  const r = parseInt(cleaned.slice(0, 2), 16)
  const g = parseInt(cleaned.slice(2, 4), 16)
  const b = parseInt(cleaned.slice(4, 6), 16)
  return { r, g, b, a: 255 }
}

function colorsMatch(
  data: Uint8ClampedArray,
  index: number,
  target: RGBA,
  tolerance: number,
): boolean {
  return (
    Math.abs(data[index] - target.r) <= tolerance &&
    Math.abs(data[index + 1] - target.g) <= tolerance &&
    Math.abs(data[index + 2] - target.b) <= tolerance &&
    Math.abs(data[index + 3] - target.a) <= tolerance
  )
}

function setPixel(
  data: Uint8ClampedArray,
  index: number,
  color: RGBA,
): void {
  data[index] = color.r
  data[index + 1] = color.g
  data[index + 2] = color.b
  data[index + 3] = color.a
}

/**
 * Scanline flood fill. Mutates `imageData` in place and returns it.
 * Tolerance is per-channel, 0-255. Use ~8 to bleed over soft AA edges without
 * pouring across hard outlines.
 */
export function floodFill(
  imageData: ImageData,
  startX: number,
  startY: number,
  fillColor: RGBA,
  tolerance: number = 8,
): ImageData {
  const { data, width, height } = imageData
  const x0 = Math.floor(startX)
  const y0 = Math.floor(startY)
  if (x0 < 0 || y0 < 0 || x0 >= width || y0 >= height) return imageData

  const startIndex = (y0 * width + x0) * 4
  const targetColor: RGBA = {
    r: data[startIndex],
    g: data[startIndex + 1],
    b: data[startIndex + 2],
    a: data[startIndex + 3],
  }

  const sameColor =
    targetColor.r === fillColor.r &&
    targetColor.g === fillColor.g &&
    targetColor.b === fillColor.b &&
    targetColor.a === fillColor.a
  if (sameColor) return imageData

  const stack: Array<[number, number]> = [[x0, y0]]

  while (stack.length > 0) {
    const [sx, sy] = stack.pop()!
    let x = sx
    while (
      x >= 0 &&
      colorsMatch(data, (sy * width + x) * 4, targetColor, tolerance)
    ) {
      x--
    }
    x++
    let spanAbove = false
    let spanBelow = false
    while (
      x < width &&
      colorsMatch(data, (sy * width + x) * 4, targetColor, tolerance)
    ) {
      setPixel(data, (sy * width + x) * 4, fillColor)

      if (sy > 0) {
        const aboveMatch = colorsMatch(
          data,
          ((sy - 1) * width + x) * 4,
          targetColor,
          tolerance,
        )
        if (!spanAbove && aboveMatch) {
          stack.push([x, sy - 1])
          spanAbove = true
        } else if (spanAbove && !aboveMatch) {
          spanAbove = false
        }
      }

      if (sy < height - 1) {
        const belowMatch = colorsMatch(
          data,
          ((sy + 1) * width + x) * 4,
          targetColor,
          tolerance,
        )
        if (!spanBelow && belowMatch) {
          stack.push([x, sy + 1])
          spanBelow = true
        } else if (spanBelow && !belowMatch) {
          spanBelow = false
        }
      }

      x++
    }
  }

  return imageData
}
